import { SafeReportResponse } from "../Conversion";
import { formatAbbreviatedMoneyExact, formatExactMoneyDollars } from "../Money";
import * as client from "../../../client";
import { DBPerilType } from "../general-input/util";

export type PerilDataSource = client.PerilTypePremium["source"];

export const PERIL_TYPE_ORDER: DBPerilType[] = [
  "aop",
  "named_storm",
  "earthquake",
  "earth_movement",
  "flood",
  "wildfire",
  "scs",
  "wind_and_hail",
  "equipment_breakdown",
];

const PERIL_CODE_DISPLAY_NAMES: Record<string, string> = {
  PWH: "Hurricane - Wind Only",
  PWH_PSH: "Hurricane - Wind and Surge",
  PES: "Earthquake - Ground Shake",
  PFL: "Inland Flood",
  PFL_PSH: "Flood - Inland and Surge",
  PWB: "Wildfire",
  PWW: "Winter Storm",
  PWT: "Severe Thunderstorm",
  AOP: "All Other Perils",
  EBP: "Equipment Breakdown",
};

export const perilCodeDisplayName = (code: string): string =>
  PERIL_CODE_DISPLAY_NAMES[code] ?? code;

export const compareLayers = (a: client.Layer, b: client.Layer): number => {
  // 1. Primary (starting_point=0) before excess
  const aIsPrimary = a.starting_point === 0 ? 0 : 1;
  const bIsPrimary = b.starting_point === 0 ? 0 : 1;
  if (aIsPrimary !== bIsPrimary) return aIsPrimary - bIsPrimary;

  // 2. By starting point ascending
  if (a.starting_point !== b.starting_point)
    return a.starting_point - b.starting_point;

  // 3. By size (range) ascending
  return a.range - b.range;
};

export const sortedLayersFromReport = (
  specialtyPropertyInfo: SafeReportResponse
) => specialtyPropertyInfo.report_json.layer_info.sort(compareLayers);

// Mirrors the pricing factor (_participation_factor in premium_calc.py and
// targetSharePremium) so the displayed percent is what actually prices:
// unset or zero participation, or a zero range, means the full layer.
export const participationPercent = (layer: client.Layer): number => {
  const { participation, range } = layer;
  return participation != null && participation > 0 && range > 0
    ? Math.min(participation / range, 1) * 100
    : 100;
};

/** Whole percents print without decimals, fractional ones with two. */
export const formatParticipationPercent = (pct: number): string =>
  `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}%`;

export const readableLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;
  const isPartial =
    !!participation && participation > 0 && participation < range;
  const base =
    starting_point === 0
      ? `Primary ${formatAbbreviatedMoneyExact(range)}`
      : `${formatAbbreviatedMoneyExact(range)} xs ${formatAbbreviatedMoneyExact(
          starting_point
        )}`;
  return isPartial
    ? `${formatAbbreviatedMoneyExact(participation)} part of ${base}`
    : base;
};

export const readableLimitOfLiabilityFromLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;

  const effectiveParticipation = participation ?? range;
  const hasExcess = starting_point > 0;
  const isPartial = effectiveParticipation < range;

  let result = "";

  if (isPartial && hasExcess) {
    // e.g. $10M part of $25M x/s $25M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} part of ${formatExactMoneyDollars(range)} excess ${formatExactMoneyDollars(starting_point)}`;
  } else if (isPartial) {
    // e.g. $5M part of $10M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} part of ${formatExactMoneyDollars(range)}`;
  } else if (hasExcess) {
    // e.g. $5M x/s $5M per occurrence
    result = `${formatExactMoneyDollars(range)} excess ${formatExactMoneyDollars(starting_point)}`;
  } else {
    // e.g. $10M per occurrence
    result = formatExactMoneyDollars(range);
  }

  return `${result} per occurrence`;
};

export function hasOverlappingLayers(layers: client.Layer[]): boolean {
  return layers.some((a, i) =>
    layers.slice(i + 1).some((b) => {
      const aEnd = a.starting_point + a.range;
      const bEnd = b.starting_point + b.range;
      return a.starting_point < bEnd && aEnd > b.starting_point;
    })
  );
}

/** Soft floor for the AAL multiple. Edits (to the Multiple or the Layer Prem)
 * that would push the multiple below this prompt for confirmation rather than
 * being blocked outright. */
export const MIN_MULTIPLE = 1.33;

/** Whether each peril type is a standalone modeled cat peril. Layer-level
 * AAL, Multiple, and Loss Ratio figures cover just those. The others carry no
 * comparable model AAL: "aop" bundles the All Other Perils direct premium with
 * the Winter Storm AAL, equipment breakdown is direct premium, and the legacy
 * "wind_and_hail" was never modeled. A Record so that a new member of
 * DBPerilType must be classified here before the frontend compiles. */
const MODELED_CAT_PERIL: Record<DBPerilType, boolean> = {
  aop: false,
  named_storm: true,
  earthquake: true,
  earth_movement: true,
  flood: true,
  wildfire: true,
  scs: true,
  wind_and_hail: false,
  equipment_breakdown: false,
};
export const isModeledCatPeril = (peril: DBPerilType): boolean =>
  MODELED_CAT_PERIL[peril];

export interface AalRollupItem {
  /** Modeled AAL, or null when the model did not produce one for this peril. */
  aal: number | null;
  premium: number;
}

export interface AalRollup {
  /** Sum of the modeled AALs, or null when no peril was modeled. */
  aal: number | null;
  /** Premium of the modeled perils only. */
  premium: number;
  /** `premium / aal`, or null when there is no positive AAL to divide by. */
  multiple: number | null;
}

/** Sum of the modeled AALs, or null when none is. Unmodeled perils are left
 * out rather than counted as zero, so an all-unmodeled set has no AAL at all
 * and callers render a dash instead of a misleading figure. */
export const sumModeledAal = (
  aals: readonly (number | null)[]
): number | null => {
  const modeled = aals.filter((a): a is number => a !== null);
  return modeled.length ? modeled.reduce((s, a) => s + a, 0) : null;
};

/** The AAL multiple `premium / aal`, or null when there is no positive AAL. */
export const aalMultiple = (
  premium: number,
  aal: number | null
): number | null => (aal !== null && aal > 0 ? premium / aal : null);

/** Roll per-peril AALs and premiums up into one multiple.
 *
 * An unmodeled peril (null AAL) is dropped from both sides: its premium is
 * left out of the numerator just as its AAL is left out of the denominator.
 * Counting its premium against the other perils' AAL would inflate the
 * multiple. */
export const aalRollup = (items: readonly AalRollupItem[]): AalRollup => {
  const modeled = items.filter((i) => i.aal !== null);
  const aal = sumModeledAal(modeled.map((i) => i.aal));
  const premium = modeled.reduce((s, i) => s + i.premium, 0);
  return { aal, premium, multiple: aalMultiple(premium, aal) };
};
